// Marketplace.js
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  InputAdornment,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { ArrowUp } from 'lucide-react';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import {
  getPublishedProducts,
  publishProduct,
  unpublishProduct,
  recordProductView
} from '../../services/AGTUps-product-services/marketplaceService';
import chatService from '../../services/AGTUps-product-services/chatService';

// Función para obtener colores según el modo
const getThemeColors = (mode, isWallpaperMode = false) => ({
  ...(mode === 'dark'
    ? {
        label: { primary: '#FFFFFF' },
        system: {
          grey: '#8E8E93',
          grey3: '#464649',
          green: '#2DD257',
          white: '#FFFFFF',
          black: '#000000',
        },
        bg: { primaryElevated: '#000000' },
        fill: { tertiary: '#767680' },
        ui: { accent: isWallpaperMode ? '#FFFFFF' : '#0385FF', base: '#272729' },
      }
    : {
        label: { primary: '#000000' },
        system: {
          grey: '#8E8E93',
          grey3: '#C7C7CC',
          green: '#31C859',
          white: '#FFFFFF',
          black: '#000000',
        },
        bg: { primaryElevated: '#FFFFFF' },
        fill: { tertiary: '#767680' },
        ui: { accent: isWallpaperMode ? '#FFFFFF' : '#007BFE', base: '#F2F2F7' },
      }),
});

// Contenedor con scroll - transparente para mostrar wallpaper
const StyledScrollContainer = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    height: 'calc(100vh - 180px)',
    overflowY: 'auto',
    width: '100%',
    padding: '24px',
    backgroundColor: isWallpaperMode ? 'transparent' : undefined,
    '&::-webkit-scrollbar': { width: '8px' },
    '&::-webkit-scrollbar-track': { background: 'transparent' },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(0, 0, 0, 0.2)',
      borderRadius: '4px',
    },
  };
});

const ProductCard = styled(Paper)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  const colors = getThemeColors(theme.palette.mode);

  return {
    padding: theme.spacing(2),
    borderRadius: theme.spacing(3),
    textAlign: 'center',
    height: 340,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.25)'
      : colors.ui.base,
    backdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    boxShadow: 'none',
  };
});

const ImageContainer = styled(Box)(({ theme }) => ({
  height: 180,
  maxWidth: '100%',
  overflow: 'hidden',
  marginBottom: theme.spacing(2),
  borderRadius: theme.spacing(2),
  position: 'relative',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: theme.spacing(2),
  },
}));

// Componente para mostrar el companyName como overlay
const OverlayCompany = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1),
  right: theme.spacing(1),
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  color: '#FFFFFF',
  padding: theme.spacing(0.5, 1),
  borderRadius: theme.spacing(1),
  fontSize: '0.75rem',
}));

const StyledTabs = styled(Tabs)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  const colors = getThemeColors(theme.palette.mode, isWallpaperMode);
  return {
    '& .MuiTabs-indicator': {
      height: 3,
      borderRadius: '3px 3px 0 0',
      backgroundColor: colors.ui.accent,
    },
    '& .MuiTab-root': {
      textTransform: 'none',
      fontSize: '1rem',
      fontWeight: 500,
      color: colors.system.grey,
      '&.Mui-selected': {
        color: colors.ui.accent,
      },
    },
  };
});

const SearchInput = styled(TextField)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      backgroundColor: isWallpaperMode
        ? 'rgba(128, 128, 128, 0.3)'
        : getThemeColors(theme.palette.mode).ui.base,
      backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
      WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
      border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
      '& fieldset': {
        border: 'none',
      },
    },
  };
});

const RoundedTextField = styled(TextField)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  const colors = getThemeColors(theme.palette.mode);

  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.spacing(3),
      backgroundColor: isWallpaperMode
        ? 'rgba(128, 128, 128, 0.25)'
        : theme.palette.mode === 'light'
          ? colors.ui.base
          : colors.bg.primaryElevated,
      backdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
      WebkitBackdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
      border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
    },
  };
});

const StyledDialog = styled(Dialog)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  return {
    '& .MuiDialog-paper': {
      borderRadius: theme.spacing(3),
      boxShadow: 'none',
      backgroundColor: isWallpaperMode
        ? 'rgba(128, 128, 128, 0.3)'
        : (theme.palette.mode === 'dark' ? '#1C1C1E' : '#FFFFFF'),
      backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
      WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
      border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    },
    '& .MuiDialogTitle-root': {
      padding: theme.spacing(3),
    },
    '& .MuiDialogContent-root': {
      padding: theme.spacing(3),
    },
  };
});

const Marketplace = ({ products }) => {
  const theme = useTheme();
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  const colors = getThemeColors(theme.palette.mode, isWallpaperMode);
  
  // ---- CAMBIO: Usamos un estado para el userId ----
  const [userId, setUserId] = useState('');

  // ---- NUEVO: Estado para productos de otros usuarios ----
  const [otherUsersProducts, setOtherUsersProducts] = useState([]);

  // ---- NUEVO: useEffect para obtener el userId de manera robusta ----
  useEffect(() => {
    const getUserId = () => {
      // Método 1: Clave predeterminada de Firebase
      try {
        const firebaseAuthKey = 'firebase:authUser:AIzaSyCl_QY3Y0RYRNuMc541mwhBlMW2VbLiqhQ:[DEFAULT]';
        const storedUser = localStorage.getItem(firebaseAuthKey);
        if (storedUser) {
          const parsedUser = JSON.parse(storedUser);
          if (parsedUser && parsedUser.uid) {
            console.log('[DEBUG] userId de clave predeterminada:', parsedUser.uid);
            return parsedUser.uid;
          }
        }
      } catch (e) {
        console.error('[DEBUG] Error al leer localStorage:', e);
      }
      
      // Método 2: Buscar en cualquier clave de Firebase
      try {
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.includes('firebase:authUser:')) {
            try {
              const userData = JSON.parse(localStorage.getItem(key));
              if (userData && userData.uid) {
                console.log('[DEBUG] userId de clave alternativa:', userData.uid);
                return userData.uid;
              }
            } catch (e) {}
          }
        }
      } catch (e) {
        console.error('[DEBUG] Error buscando claves alternativas:', e);
      }
      
      return '';
    };
    
    const id = getUserId();
    if (id) {
      setUserId(id);
      console.log('[DEBUG] userId establecido:', id);
    } else {
      console.error('[DEBUG] No se pudo obtener userId');
    }
  }, []);

  const [allPublishedProducts, setAllPublishedProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  // Por defecto se muestra la pestaña "For You" (índice 1)
  const [activeTab, setActiveTab] = useState(1);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [descExpanded, setDescExpanded] = useState(false);
  // Mensaje inicial de chat
  const [chatMessage, setChatMessage] = useState('Hola, ¿todavía está disponible?');
  const [searchQuery, setSearchQuery] = useState('');
  // Estado para el modal de negociación con IA
  const [negotiationModalOpen, setNegotiationModalOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);

  // Función para mostrar mensajes al usuario
  const showMessage = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // ---- MODIFICADO: fetchAllPublishedProducts para obtener SOLO productos propios ----
  const fetchAllPublishedProducts = useCallback(async () => {
    if (!userId) {
      console.log('[DEBUG] No hay userId, no se pueden obtener productos');
      return;
    }
    
    setLoading(true);
    try {
      console.log('[DEBUG] Obteniendo MIS productos publicados, userId:', userId);
      // Ahora específicamente pedimos solo los productos del usuario actual (excludeOwn=false)
      const response = await getPublishedProducts(false);
      
      // Verificar si el userId del backend coincide con el nuestro
      if (response.currentUserId && response.currentUserId !== userId) {
        console.warn('[DEBUG] ¡Discrepancia de userId!', {
          frontend: userId,
          backend: response.currentUserId
        });
        
        // Si confiamos más en el backend, actualizamos nuestro userId
        setUserId(response.currentUserId);
        showMessage('Se ha actualizado la identificación del usuario', 'info');
      }
      
      // Verificar que todos los productos tengan el campo isOwnedByCurrentUser
      const verifiedProducts = (response.data || []).map(product => ({
        ...product,
        isOwnedByCurrentUser: product.userId === userId || product.isOwnedByCurrentUser === true
      }));
      
      setAllPublishedProducts(verifiedProducts);
      
      console.log('[DEBUG] Mis productos recibidos:', verifiedProducts.length);
    } catch (error) {
      console.error('Error fetching my published products:', error);
      setAllPublishedProducts([]);
      showMessage('Error al cargar tus productos', 'error');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // ---- NUEVO: función para obtener productos de OTROS usuarios ----
  const fetchOtherUsersProducts = useCallback(async () => {
    if (!userId) {
      console.log('[DEBUG] No hay userId, no se pueden obtener productos de otros');
      return;
    }
    
    setLoading(true);
    try {
      console.log('[DEBUG] Obteniendo productos de OTROS usuarios, userId:', userId);
      // Específicamente pedimos EXCLUIR los productos del usuario actual (excludeOwn=true)
      const response = await getPublishedProducts(true);
      
      const otherProducts = response.data || [];
      setOtherUsersProducts(otherProducts);
      
      console.log('[DEBUG] Productos de otros usuarios recibidos:', otherProducts.length);
    } catch (error) {
      console.error('Error fetching other users products:', error);
      setOtherUsersProducts([]);
      showMessage('Error al cargar productos de otros usuarios', 'error');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // ---- MODIFICADO: useEffect para cargar ambos tipos de productos al iniciar ----
  useEffect(() => {
    if (userId) {
      fetchAllPublishedProducts();
      fetchOtherUsersProducts();
    }
  }, [userId, products, fetchAllPublishedProducts, fetchOtherUsersProducts]);

  // ---- NUEVO: useEffect para actualizar productos cuando cambia la pestaña ----
  useEffect(() => {
    if (userId) {
      if (activeTab === 0) {
        // Si estamos en "Mis Productos", actualizamos nuestros productos
        fetchAllPublishedProducts();
      } else {
        // Si estamos en "Otros Usuarios", actualizamos productos de otros
        fetchOtherUsersProducts();
      }
    }
  }, [activeTab, userId, fetchAllPublishedProducts, fetchOtherUsersProducts]);

  // Publica un producto (desde los que aún no están publicados)
  const handlePublishProduct = async (product) => {
    try {
      const response = await publishProduct(product.id, product);
      if (response.success) {
        showMessage('Producto publicado correctamente');
        fetchAllPublishedProducts();
      } else {
        showMessage('Error al publicar el producto', 'error');
      }
    } catch (error) {
      console.error('Error publishing product:', error);
      showMessage('Error al publicar el producto', 'error');
    }
  };

  // Despublica un producto ya publicado
  const handleUnpublishProduct = async (product) => {
    try {
      const response = await unpublishProduct(product.productId);
      if (response.success) {
        showMessage('Producto despublicado correctamente');
        fetchAllPublishedProducts();
      } else {
        showMessage('Error al despublicar el producto', 'error');
      }
    } catch (error) {
      console.error('Error unpublishing product:', error);
      showMessage('Error al despublicar el producto', 'error');
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Abre el modal y selecciona el producto
  const handleOpenModal = async (product) => {
    try {
      // Solo registrar la visualización si el producto no es del usuario actual
      if (product.userId !== userId) {
        await recordProductView(product.productId);
      }
      
      // Continuar con la lógica existente
      setSelectedProduct(product);
      setDescExpanded(false);
      setModalOpen(true);
    } catch (error) {
      console.error('Error al registrar visualización:', error);
      // Continuar mostrando el modal incluso si falla el registro
      setSelectedProduct(product);
      setDescExpanded(false);
      setModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedProduct(null);
  };

  const handleToggleDescription = () => {
    setDescExpanded((prev) => !prev);
  };

  /**
   * Envía el mensaje usando REST (chatService).
   */
  const handleSendMessage = async () => {
    if (!chatMessage.trim()) return;

    const otherUserId = selectedProduct?.userId;

    // Impedir que el usuario se envíe mensaje a sí mismo
    if (otherUserId === userId) {
      showMessage('No puedes enviarte mensajes a ti mismo', 'error');
      return;
    }

    setIsSending(true);
    try {
      const chatId = chatService.formatChatId(userId, otherUserId);

      const messageData = {
        chatId,
        senderId: userId,
        receiverId: otherUserId,
        message: chatMessage.trim(),
        productName: selectedProduct?.name,
        productImage: selectedProduct?.imageUrl || '',
        otherCompanyName: selectedProduct?.companyName,
      };

      const response = await chatService.sendMessage(messageData);

      if (response.success) {
        showMessage('Mensaje enviado correctamente');
        setChatMessage('');
      }
    } catch (error) {
      console.error('Error al enviar el mensaje:', error);
      showMessage(error.message || 'Error al enviar el mensaje', 'error');
    } finally {
      setIsSending(false);
    }
  };

  // --- Funciones para negociación con IA ---
  const handleNegotiateWithAI = async () => {
    if (!selectedProduct) return;
    if (selectedProduct.userId === userId) {
      showMessage('No puedes negociar con tu propio producto', 'error');
      return;
    }
  
    // Construir chatId
    const chatId = chatService.formatChatId(userId, selectedProduct.userId);
  
    setIsSending(true);
    try {
      const response = await chatService.negotiateWithAI(
        chatId,
        selectedProduct.productId,
        selectedProduct.userId // <-- AQUI pasas el ownerId
      );
      if (response.success) {
        showMessage('La IA ha iniciado la negociación. Revisa el chat.');
      } else {
        showMessage('Error al iniciar negociación con IA', 'error');
      }
    } catch (error) {
      console.error('Error negociando con IA:', error);
      showMessage(error.message || 'Error al negociar con IA', 'error');
    } finally {
      setIsSending(false);
      setNegotiationModalOpen(false);
    }
  };

  const handleOpenNegotiationModal = () => {
    setNegotiationModalOpen(true);
  };

  const handleCloseNegotiationModal = () => {
    setNegotiationModalOpen(false);
  };
  // ----------------------------------------

  // ---- MODIFICADO: Cómo se filtran los productos propios ----
  const publishedList = allPublishedProducts || [];
  const myPublished = publishedList.filter((item) => 
    item.isOwnedByCurrentUser || item.userId === userId
  );
  const myInventoryNotPublished = products
    ? products.filter((product) =>
        !publishedList.some(
          (published) =>
            published.productId === product.id ||
            published.productId === product.productId
        )
      )
    : [];

  // ---- MODIFICADO: Qué productos mostrar en cada pestaña ----
  let displayedProducts =
    activeTab === 0
      ? myPublished.length > 0
        ? myPublished
        : myInventoryNotPublished
      : otherUsersProducts; // Ahora mostramos otherUsersProducts en "For You"

  const filteredDisplayedProducts = displayedProducts.filter((product) =>
    product.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <StyledScrollContainer
      sx={{
        backgroundColor: isWallpaperMode ? 'transparent' : colors.bg.primaryElevated
      }}
    >
      <Typography
        variant="h4"
        align="center"
        gutterBottom
        sx={{
          fontWeight: 600,
          color: colors.label.primary,
          mb: 2,
        }}
      >
        Marketplace
      </Typography>

      {/* Input de búsqueda */}
      <Box sx={{ width: '100%', mb: 4 }}>
        <SearchInput
          fullWidth
          placeholder="Buscar productos..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  fill="none"
                  stroke={colors.system.grey}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-search"
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* ---- MODIFICADO: Nombres de pestañas más claros ---- */}
      <StyledTabs value={activeTab} onChange={handleTabChange} centered>
        <Tab label="My Publishied" />
        <Tab label="For You" />
      </StyledTabs>

      {/* ---- NUEVO: Indicador de carga ---- */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress
            sx={{ color: (theme) => theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : theme.palette.primary.main }}
          />
          <Typography variant="body1" sx={{ ml: 2, color: colors.system.grey }}>
            Cargando productos...
          </Typography>
        </Box>
      )}

      {activeTab === 0 ? (
        <>
          {myPublished.length > 0 && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, color: colors.label.primary }}>
                Productos Publicados
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                  gap: 3,
                }}
              >
                {filteredDisplayedProducts.map((item) => (
                  <ProductCard key={item.productId}>
                    <ImageContainer>
                      {item.imageUrl && <img src={item.imageUrl} alt={item.name} />}
                      {item.companyName && (
                        <OverlayCompany>{item.companyName}</OverlayCompany>
                      )}
                      {/* ---- MODIFICADO: Visualización mejorada de "Mi producto" ---- */}
                      {(item.isOwnedByCurrentUser || item.userId === userId) && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 8,
                            left: 8,
                            backgroundColor: 'rgba(239, 108, 0, 0.8)', // Naranja más visible
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: 1,
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            zIndex: 10
                          }}
                        >
                          Mi producto ({item.userId.substring(0, 4)}...)
                        </Box>
                      )}
                    </ImageContainer>
                    <Typography variant="h6" sx={{ color: colors.label.primary }}>
                      {item.name}
                    </Typography>
                    <Button
                      variant="text"
                      size="small"
                      startIcon={<TouchAppIcon />}
                      onClick={() => handleOpenModal(item)}
                      sx={{
                        color: colors.ui.accent,
                        boxShadow: 'none',
                        '&:hover': {
                          backgroundColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.1)' : undefined
                        }
                      }}
                    >
                      Click for more
                    </Button>
                    <Box sx={{ mt: 1 }}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleUnpublishProduct(item)}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          backgroundColor: 'red',
                          color: colors.system.white,
                          boxShadow: 'none',
                        }}
                      >
                        Despublicar
                      </Button>
                    </Box>
                    <Typography variant="subtitle1" sx={{ color: colors.label.primary, mt: 1 }}>
                      ${item.price}
                    </Typography>
                  </ProductCard>
                ))}
              </Box>
            </Box>
          )}

          {myInventoryNotPublished.length > 0 && (
            <Box sx={{ mt: 6 }}>
              <Typography variant="h6" sx={{ mb: 2, color: colors.label.primary }}>
                Productos por Publicar
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                  gap: 3,
                }}
              >
                {myInventoryNotPublished
                  .filter((product) =>
                    product.name.toLowerCase().includes(searchQuery.toLowerCase())
                  )
                  .map((item) => (
                    <ProductCard key={item.id}>
                      <ImageContainer>
                        {item.imageUrl && <img src={item.imageUrl} alt={item.name} />}
                        {item.companyName && (
                          <OverlayCompany>{item.companyName}</OverlayCompany>
                        )}
                      </ImageContainer>
                      <Typography variant="h6" sx={{ color: colors.label.primary }}>
                        {item.name}
                      </Typography>
                      <Button
                        variant="text"
                        size="small"
                        startIcon={<TouchAppIcon />}
                        onClick={() => handleOpenModal(item)}
                        sx={{
                          color: colors.ui.accent,
                          boxShadow: 'none',
                          '&:hover': {
                            backgroundColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.1)' : undefined
                          }
                        }}
                      >
                        Click for more
                      </Button>
                      <Box sx={{ mt: 1 }}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handlePublishProduct(item)}
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            backgroundColor: isWallpaperMode ? 'rgba(128, 128, 128, 0.3)' : colors.ui.accent,
                            color: '#FFFFFF',
                            backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
                            WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
                            border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
                            boxShadow: 'none',
                            '&:hover': {
                              backgroundColor: isWallpaperMode ? 'rgba(128, 128, 128, 0.4)' : undefined
                            }
                          }}
                        >
                          Publicar producto
                        </Button>
                      </Box>
                      <Typography variant="subtitle1" sx={{ color: colors.label.primary, mt: 1 }}>
                        ${item.price}
                      </Typography>
                    </ProductCard>
                  ))}
              </Box>
            </Box>
          )}

          {myPublished.length === 0 && myInventoryNotPublished.length === 0 && !loading && (
            <Typography variant="body1" sx={{ color: colors.system.grey, mt: 4, textAlign: 'center' }}>
              No tienes productos en tu inventario.
            </Typography>
          )}
        </>
      ) : (
        <Box sx={{ mt: 4 }}>
          {filteredDisplayedProducts.length > 0 ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: 3,
              }}
            >
              {filteredDisplayedProducts.map((item) => (
                <ProductCard key={item.productId}>
                  <ImageContainer>
                    {item.imageUrl && <img src={item.imageUrl} alt={item.name} />}
                    {item.companyName && (
                      <OverlayCompany>{item.companyName}</OverlayCompany>
                    )}
                    {/* Esta condición nunca debería ser true en la pestaña "Otros Usuarios" */}
                    {(item.isOwnedByCurrentUser || item.userId === userId) && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          left: 8,
                          backgroundColor: 'rgba(239, 108, 0, 0.8)',
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: 1,
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          zIndex: 10
                        }}
                      >
                        Mi producto ({item.userId.substring(0, 4)}...)
                      </Box>
                    )}
                  </ImageContainer>
                  <Typography variant="h6" sx={{ color: colors.label.primary }}>
                    {item.name}
                  </Typography>
                  <Button
                    variant="text"
                    size="small"
                    startIcon={<TouchAppIcon />}
                    onClick={() => handleOpenModal(item)}
                    sx={{
                      color: colors.ui.accent,
                      boxShadow: 'none',
                      '&:hover': {
                        backgroundColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.1)' : undefined
                      }
                    }}
                  >
                    Click for more
                  </Button>
                  <Typography variant="subtitle1" sx={{ color: colors.label.primary, mt: 1 }}>
                    ${item.price}
                  </Typography>
                </ProductCard>
              ))}
            </Box>
          ) : !loading && (
            <Typography variant="body1" sx={{ color: colors.system.grey, textAlign: 'center', mt: 4 }}>
              No hay productos publicados por otros usuarios.
            </Typography>
          )}
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Modal principal de producto */}
      <StyledDialog open={modalOpen} onClose={handleCloseModal} fullWidth maxWidth="sm">
        <DialogTitle sx={{ color: colors.label.primary }}>
          {selectedProduct?.name}
        </DialogTitle>
        <DialogContent dividers>
          {selectedProduct?.imageUrl && (
            <Box
              component="img"
              src={selectedProduct.imageUrl}
              alt={selectedProduct.name}
              sx={{
                width: '100%',
                maxHeight: 300,
                objectFit: 'cover',
                borderRadius: 3,
                mb: 3,
              }}
            />
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: colors.label.primary }}>
              ${selectedProduct?.price}
            </Typography>
            <Typography variant="h6" sx={{ color: colors.system.grey }}>
              {selectedProduct?.category}
            </Typography>
          </Box>
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="body1"
              sx={{
                color: colors.label.primary,
                whiteSpace: 'pre-line',
                ...(descExpanded
                  ? {}
                  : {
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }),
              }}
            >
              {selectedProduct?.description}
            </Typography>
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Button
                variant="contained"
                size="small"
                sx={{
                  borderRadius: 3,
                  minWidth: '80px',
                  backgroundColor: isWallpaperMode ? '#FFFFFF' : colors.ui.accent,
                  color: isWallpaperMode ? '#000000' : '#FFFFFF',
                  textTransform: 'none',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: isWallpaperMode ? '#FFFFFF' : undefined,
                    boxShadow: 'none',
                  }
                }}
                onClick={handleToggleDescription}
              >
                {descExpanded ? 'See less' : 'See more'}
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', p: 3, gap: 2 }}>
          {/* Mostrar controles de chat y negociación solo si el producto NO es del usuario */}
          {activeTab === 1 && !(selectedProduct?.isOwnedByCurrentUser || selectedProduct?.userId === userId) ? (
            <>
              <RoundedTextField
                fullWidth
                value={chatMessage}
                onChange={(e) => setChatMessage(e.target.value)}
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleSendMessage}
                        edge="end"
                        sx={{
                          backgroundColor: isWallpaperMode ? '#FFFFFF' : colors.ui.accent,
                          '&:hover': {
                            backgroundColor: isWallpaperMode ? '#FFFFFF' : colors.ui.accent,
                            boxShadow: 'none',
                          },
                          borderRadius: '50%',
                          padding: 1,
                          boxShadow: 'none',
                          position: 'relative',
                          right: 8,
                        }}
                      >
                        {isSending ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <ArrowUp size={20} color={isWallpaperMode ? '#000000' : colors.system.white} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  onClick={handleOpenNegotiationModal}
                  sx={{
                    borderRadius: 3,
                    textTransform: 'none',
                    backgroundColor: isWallpaperMode ? '#FFFFFF' : colors.ui.accent,
                    color: isWallpaperMode ? '#000000' : '#FFFFFF',
                    px: 3,
                    boxShadow: 'none',
                    '&:hover': {
                      backgroundColor: isWallpaperMode ? '#FFFFFF' : undefined,
                      boxShadow: 'none',
                    }
                  }}
                >
                  Negotiate with AI
                </Button>
              </Box>
            </>
          ) : (
            (selectedProduct?.isOwnedByCurrentUser || selectedProduct?.userId === userId) && (
              <Typography
                variant="body2"
                sx={{ color: colors.system.grey, textAlign: 'center' }}
              >
                Este es uno de tus productos. No puedes enviarte mensajes a ti mismo.
              </Typography>
            )
          )}
          <Button
            onClick={handleCloseModal}
            sx={{
              mt: 1,
              color: colors.ui.accent,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.1)' : undefined
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </StyledDialog>

      {/* Modal de negociación con IA */}
      <StyledDialog
        open={negotiationModalOpen}
        onClose={handleCloseNegotiationModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ color: colors.label.primary }}>
          Negociación con IA
        </DialogTitle>
        <DialogContent dividers>
          <Typography
            variant="body1"
            sx={{ color: colors.label.primary, whiteSpace: 'pre-line' }}
          >
            Al negociar con IA, ten en cuenta que la Inteligencia Artificial negociará por ti un
            precio óptimo. Además, recuerda que el otro usuario también puede negociar con ella, por
            lo que la IA buscará un precio en el que ambos ganen.
            {'\n\n'}Por favor, revisa constantemente la pestaña activa o este chat para estar al
            tanto de cualquier cambio en la negociación.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-end', p: 3, gap: 2 }}>
          <Button
            onClick={handleCloseNegotiationModal}
            sx={{
              color: colors.ui.accent,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.1)' : undefined
              }
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleNegotiateWithAI}
            sx={{
              backgroundColor: isWallpaperMode ? '#FFFFFF' : colors.ui.accent,
              color: isWallpaperMode ? '#000000' : '#FFFFFF',
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: isWallpaperMode ? '#FFFFFF' : undefined,
                boxShadow: 'none',
              }
            }}
          >
            {isSending ? <CircularProgress size={20} color="inherit" /> : 'Confirmar'}
          </Button>
        </DialogActions>
      </StyledDialog>
    </StyledScrollContainer>
  );
};

export default Marketplace;